import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Card, Upload, Button, message, Typography, Alert, Tag, Space, Switch, Radio, Checkbox } from 'antd';
import { addUpload } from '../store/uploadHistorySlice';
import type { AppDispatch } from '../store/store';
import { InboxOutlined, CloudUploadOutlined } from '@ant-design/icons';
import type { UploadProps, UploadFile } from 'antd';
import { smartReviewService } from '../services/smartReviewService';
import { UploadResponse, ApiErrorResponse, GroupStrategy, SortDirection } from '../types';
import { NimbusLogo } from './NimbusLogo';
import './FileUpload.css';

const { Dragger } = Upload;
const { Text } = Typography;

interface FileUploadProps {
  onUploadComplete?: (data: UploadResponse) => void;
}

export const FileUpload: React.FC<FileUploadProps> = ({ onUploadComplete }) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<ApiErrorResponse | null>(null);
  const [includeImages, setIncludeImages] = useState<boolean>(true);
  const [groupStrategy, setGroupStrategy] = useState<GroupStrategy>('firm_first');
  const [direction, setDirection] = useState<SortDirection>('asc');
  const [processFirms, setProcessFirms] = useState<boolean>(true);
  const [processMarketing, setProcessMarketing] = useState<boolean>(true);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const handleFileChange: UploadProps['onChange'] = (info) => {
    setFileList(info.fileList.slice(-1));
    setUploadError(null);
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.error('Please select a file to upload');
      return;
    }

    setUploading(true);
    setUploadError(null);
    
    const file = fileList[0].originFileObj as File;
    
    try {
      // Use async upload - gets flow_id immediately
      const response = await smartReviewService.uploadFileAsync(
        file, includeImages, groupStrategy, direction, processFirms, processMarketing,
      );
      
      // Add to history immediately with polling status
      dispatch(addUpload({
        id: response.flow_id,
        flowId: response.flow_id,
        timestamp: Date.now(),
        fileName: file.name,
        success: false, // Not completed yet
        status: response.status,
        completionPercentage: 0,
        completedFiles: 0,
        totalFiles: response.estimated_files
      }));
      
      message.success('Upload initiated! Redirecting to processing status...');
      setFileList([]);
      
      // Navigate to polling page
      navigate(`/polling/${response.flow_id}`);
      
    } catch (error: any) {
      // Save failed upload to Redux store
      dispatch(addUpload({
        id: `failed_${Date.now()}`,
        flowId: `failed_${Date.now()}`,
        timestamp: Date.now(),
        fileName: file.name,
        success: false,
        status: 'failed',
        errorMessage: error?.response?.detail?.detail || error?.message
      }));
      
      setUploadError(error?.response || { detail: error?.message });
    } finally {
      setUploading(false);
    }
  };

  const uploadProps: UploadProps = {
    onRemove: () => {
      setFileList([]);
      setUploadError(null);
    },
    beforeUpload: () => false,
    onChange: handleFileChange,
    fileList,
    multiple: false,
    accept: '.xls,.xlsx,.csv',
    maxCount: 1,
    disabled: uploading,
  };

  return (
    <div className="file-upload-container">
      <Card 
        title={
          <div className="file-upload-title-container">
            <div className="top-left-header mt-3">
              <NimbusLogo className="file-upload-logo" />
            </div>
            
            <Text type="secondary" className="file-upload-subtitle">
              Upload your files for SmartReview and analysis
            </Text>
          </div>
        }
        className="file-upload-card"
      >
        <div className="file-upload-content">
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'stretch' }}>
          <div style={{ flex: '1 1 380px', display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
            <Text strong style={{ fontSize: 15, marginBottom: 8 }}>Upload a File</Text>
            <Dragger {...uploadProps} className="file-upload-dropzone">
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag file to this area to upload
              </p>
              <p className="ant-upload-hint">
                Only XLS, XLSX, and CSV files are supported. Select one file only.
              </p>
            </Dragger>
          </div>

          <div style={{ flex: '1 1 380px', display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
          <Text strong style={{ fontSize: 15, marginBottom: 8 }}>Upload options</Text>
          <div
            style={{
              background: '#fafafa',
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              padding: '16px 20px',
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              textAlign: 'left',
              flex: 1,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <Text strong style={{ width: 110, flexShrink: 0 }}>Images</Text>
              <Switch
                checked={includeImages}
                onChange={setIncludeImages}
                disabled={uploading}
              />
              <Text>Include images in generated files</Text>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <Text strong style={{ width: 110, flexShrink: 0 }}>Categories</Text>
              <Checkbox
                checked={processFirms}
                onChange={(e) => setProcessFirms(e.target.checked)}
                disabled={uploading || (processFirms && !processMarketing)}
              >
                Firm files
              </Checkbox>
              <Checkbox
                checked={processMarketing}
                onChange={(e) => setProcessMarketing(e.target.checked)}
                disabled={uploading || (processMarketing && !processFirms)}
              >
                Marketing files
              </Checkbox>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <Text strong style={{ width: 110, flexShrink: 0 }}>Order</Text>
              <Radio.Group
                value={groupStrategy}
                onChange={(e) => setGroupStrategy(e.target.value)}
                disabled={uploading || !processFirms || !processMarketing}
                options={[
                  { label: 'Firm first', value: 'firm_first' },
                  { label: 'Marketing first', value: 'marketing_first' },
                  { label: 'All together', value: 'mixed' },
                ]}
                optionType="button"
                buttonStyle="solid"
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <Text strong style={{ width: 110, flexShrink: 0 }}>Direction</Text>
              <Radio.Group
                value={direction}
                onChange={(e) => setDirection(e.target.value)}
                disabled={uploading}
                options={[
                  { label: 'ASC (A → Z)', value: 'asc' },
                  { label: 'DESC (Z → A)', value: 'desc' },
                ]}
                optionType="button"
                buttonStyle="solid"
              />
            </div>
          </div>
          </div>
          </div>

          <Button
            type="primary"
            onClick={handleUpload}
            loading={uploading}
            disabled={fileList.length === 0}
            size="large"
            icon={<CloudUploadOutlined />}
            className="file-upload-button"
          >
            {uploading ? 'Uploading...' : 'Upload File'}
          </Button>

          {uploadError && (() => {
            const missingColumns = uploadError.detail?.missing_columns;
            const hasMissingColumns = missingColumns && missingColumns.length > 0;
            
            if (hasMissingColumns) {
              return (
                <Alert
                  type="error"
                  showIcon
                  style={{ marginTop: 16 }}
                  description={
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 8 }}>
                        Missing Required Columns
                      </div>
                      
                      <Text style={{ display: 'block', marginBottom: 12 }}>
                        Your file is missing the following required columns:
                      </Text>
                      
                      <div style={{ marginBottom: 12 }}>
                        <Space size={[8, 8]} wrap>
                          {missingColumns!.map((columnName: string, index: number) => (
                            <Tag 
                              key={index}
                              style={{
                                backgroundColor: '#fff2e6',
                                border: '1px solid #d48806',
                                borderRadius: '4px',
                                color: '#d48806',
                                fontWeight: 500,
                                fontSize: '12px',
                                padding: '2px 8px'
                              }}
                            >
                              {columnName}
                            </Tag>
                          ))}
                        </Space>
                      </div>
                      
                      <Text 
                        type="secondary" 
                        style={{ 
                          fontSize: '12px',
                          fontStyle: 'italic'
                        }}
                      >
                        Please add these columns to your file and try uploading again.
                      </Text>
                    </div>
                  }
                />
              );
            } else {
              return (
                <Alert
                  type="error"
                  showIcon
                  style={{ marginTop: 16 }}
                  description={
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 8 }}>
                        File Upload Error
                      </div>
                      Something went wrong with this file, try another one.
                    </div>
                  }
                />
              );
            }
          })()}
        </div>
      </Card>
    </div>
  );
};